import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useCommentsPreference,
  useDiffEntries,
  useDiffEntriesWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useGetCollapsedDiffStatus,
  useLineSpacingPreference,
  useProgressiveDiffActions,
  useSelectedPathDigest,
  useSplitPreference,
  useThreadSizeInfoMap,
} from '../stores/selectors'

import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {defaultRangeExtractor, type Range, useWindowVirtualizer} from '@tanstack/react-virtual'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'
import {VirtualizedDiffRow} from './PullRequestVirtualizedDiffRow'
import virtualizedStyles from './PullRequestVirtualizedDiffsList.module.css'
import {DIFF_LIST_GAP, estimateDiffSize} from '../helpers/estimate-diff-size'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BASE_DIFF_MARGIN_TOP_PADDING, STICKY_HEADER_HEIGHT} from '../helpers/constants'

export interface PullRequestVirtualizedDiffsListProps {
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  'use no memo'

  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()
  const splitPreference = useSplitPreference()
  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const filteredDiffEntries = useDiffEntriesWithFilter()
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue, loadMore} = useProgressiveDiffActions()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const diffEntries = useDiffEntries()
  const threadSizeInfoMap = useThreadSizeInfoMap()

  const totalVisibleEntries = filteredDiffEntries.length
  const shouldShowWhimsy = totalVisibleEntries >= 15
  const getCollapsedDiffStatus = useGetCollapsedDiffStatus()
  const selectedPathDigest = useSelectedPathDigest()

  // Feature flags - declared before useCallback that depends on them
  const isDebugVirtualizationEnabled = isFeatureEnabled('pull_request_files_debug_virtualization')
  const isContainmentEnabled = isFeatureEnabled('pull_request_files_virtualization_containment')
  // Enables more accurate height estimation using thread reply counts, collapsed state, and split view mode
  const isAccurateSizeEstimatesEnabled = isFeatureEnabled('pull_request_files_accurate_size_estimates')

  useEffect(() => {
    startQueue()
  }, [startQueue])

  /**
   * Estimate size in pixels of each diff entry.
   */
  const estimateVirtualDiffSize = useCallback(
    (index: number) => {
      const progressiveDiffEntry = filteredDiffEntries[index]
      if (!progressiveDiffEntry) return 0

      const diffSummary = allDiffSummaries[progressiveDiffEntry.path ?? '']
      const isCollapsed = getCollapsedDiffStatus(progressiveDiffEntry.path ?? '')

      return estimateDiffSize({
        diffSummary,
        commentPreference,
        lineSpacingPreference,
        isCollapsed,
        // Only use enhanced estimation when feature flag is enabled
        threadSizeInfoMap: isAccurateSizeEstimatesEnabled ? threadSizeInfoMap : undefined,
        isSplitView: isAccurateSizeEstimatesEnabled && splitPreference === 'split',
      })
    },
    [
      filteredDiffEntries,
      allDiffSummaries,
      getCollapsedDiffStatus,
      commentPreference,
      lineSpacingPreference,
      splitPreference,
      threadSizeInfoMap,
      isAccurateSizeEstimatesEnabled,
    ],
  )

  const getItemKey = useCallback(
    (index: number) => filteredDiffEntries[index]?.pathDigest ?? index,
    [filteredDiffEntries],
  )

  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const defaultOverscan = 5

  const overscan = useMemo(() => {
    if (diffEntries && Object.keys(diffEntries).length === diffSummariesCount) {
      return defaultOverscan
    }
    return 0
  }, [diffEntries, diffSummariesCount])

  const pathDigestToIndex = useMemo(() => {
    const indexMap = new Map<string, number>()

    for (const [index, entry] of filteredDiffEntries.entries()) {
      indexMap.set(entry.pathDigest, index)
    }

    return indexMap
  }, [filteredDiffEntries])

  /**
   * Custom range extractor that ensures focused and selected diffs stay in the virtual window.
   * This prevents focused elements from being unmounted during scrolling, which is critical
   * for keyboard navigation and accessibility.
   */
  const rangeExtractor = useCallback(
    (range: Range) => {
      // Find the diff that currently has focus within it
      const focusedPathDigest = document
        .querySelector('[data-path-digest]:focus-within')
        ?.getAttribute('data-path-digest')

      const focusedDiffIndex = focusedPathDigest ? (pathDigestToIndex.get(focusedPathDigest) ?? -1) : -1

      if (focusedDiffIndex < 0) {
        return defaultRangeExtractor(range)
      }

      // Extend the default range to include focused and selected indices
      const nextRange = new Set([...defaultRangeExtractor(range), ...[focusedDiffIndex].filter(index => index > -1)])

      return Array.from(nextRange).sort((a, b) => a - b)
    },
    [pathDigestToIndex],
  )

  const virtualizer = useWindowVirtualizer({
    count: filteredDiffEntries.length,
    estimateSize: estimateVirtualDiffSize,
    getItemKey,
    overscan,
    // We use a the sticky header height and base diff marging top padding
    // to ensure that we scroll to the diff header instead of a random line in a diff entry.
    scrollPaddingStart: BASE_DIFF_MARGIN_TOP_PADDING + STICKY_HEADER_HEIGHT,
    scrollMargin,
    gap: DIFF_LIST_GAP,
    rangeExtractor,
    debug: isDebugVirtualizationEnabled,
    // Note: We intentionally do NOT use useAnimationFrameWithResizeObserver.
    // The tanstack/virtual docs recommend against it as ResizeObserver already
    // runs at an optimal time (after layout, before paint). Enabling it adds
    // ~16ms delay and provides no batching benefit.
    onChange(instance) {
      instance.shouldAdjustScrollPositionOnItemSizeChange = (_item, _delta, _inst) => {
        // Don't adjust scroll position on item size changes
        // This prevents jarring viewport jumps when items resize
        return false
      }
    },
  })

  const virtualItems = virtualizer.getVirtualItems()

  // Cache the path we've already scrolled to so we do not keep snapping back while the user scrolls
  const hasScrolledToSelectionRef = useRef<string | null>(null)

  // Scroll to the selected diff
  useEffect(() => {
    if (!selectedPathDigest) {
      hasScrolledToSelectionRef.current = null
      return
    }

    if (hasScrolledToSelectionRef.current === selectedPathDigest) {
      return
    }

    const targetIndex = pathDigestToIndex.get(selectedPathDigest)
    if (targetIndex === undefined) return

    const targetEntry = filteredDiffEntries[targetIndex]

    if (targetEntry?.loadingStatus === 'NotLoaded') {
      loadMore(targetEntry)
      return
    }

    // Check if the target diff is actually visible in the natural viewport range.
    // We can't just check virtualItems because the rangeExtractor forcibly adds selected items
    // to keep them in the DOM (for accessibility). Instead, check if the item's position
    // falls within the current scroll viewport.
    const virtualItem = virtualItems.find(item => item.index === targetIndex)
    const isActuallyInViewport =
      virtualItem &&
      virtualItem.start >= (virtualizer.scrollOffset ?? 0) - virtualItem.size &&
      virtualItem.start <= (virtualizer.scrollOffset ?? 0) + (virtualizer.scrollRect?.height ?? 0)

    if (isActuallyInViewport) {
      hasScrolledToSelectionRef.current = selectedPathDigest
      return
    }

    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(targetIndex, {align: 'start'})
    })

    hasScrolledToSelectionRef.current = selectedPathDigest
  }, [selectedPathDigest, pathDigestToIndex, filteredDiffEntries, loadMore, virtualizer, virtualItems])

  // Load unloaded entries that are currently visible
  useEffect(() => {
    let firstUnloadedIndex = -1

    for (const item of virtualItems) {
      const entry = filteredDiffEntries[item.index]
      if (entry?.loadingStatus === 'NotLoaded') {
        firstUnloadedIndex = item.index
      }
    }

    if (firstUnloadedIndex === -1) return

    const entry = filteredDiffEntries[firstUnloadedIndex]
    if (!entry) return

    loadMore(entry)
  }, [filteredDiffEntries, loadMore, virtualItems])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffEntries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc data-testid="virtualized-diffs-list" className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        className={virtualizedStyles['virtualized-diff-row']}
        data-containment-enabled={isContainmentEnabled || undefined}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems.map(virtualRow => {
          const diffEntry = filteredDiffEntries[virtualRow.index]
          return (
            <VirtualizedDiffRow
              key={virtualRow.key}
              virtualRow={virtualRow}
              virtualizedDiffEntry={diffEntry}
              diffEntry={diffEntries[diffEntry?.path ?? '']}
              diffSummary={allDiffSummaries[diffEntry?.path ?? '']}
              scrollMargin={virtualizer.options.scrollMargin}
              measureElement={virtualizer.measureElement}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              repository={repository}
              virtualWindowHeight={virtualizer.scrollRect?.height}
              isContainmentEnabled={isContainmentEnabled}
            />
          )
        })}
      </div>
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

try{ PullRequestVirtualizedDiffsList.displayName ||= 'PullRequestVirtualizedDiffsList' } catch {}